import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useAutorizzazioni from '../hooks/useAutorizzazioni';
import useNetworkHierarchy from '../hooks/useNetworkHierarchy';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import API_CONFIG from '../config/api';
import '../styles/dashboard.css';
import {
  UsersIcon,
  CalculatorIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  CurrencyEuroIcon,
  ShoppingCartIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  TruckIcon,
  CalendarDaysIcon,
  UserPlusIcon,
  PresentationChartLineIcon,
  CogIcon,
  ClipboardIcon,
  BellIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  SparklesIcon,
  RocketLaunchIcon,
  StarIcon,
  ArrowPathIcon,
  FireIcon,
  LightBulbIcon,
  HeartIcon,
  ArrowRightIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardNlt = ({ utente }) => {
  const {
    isPaginaAbilitata,
    isSottopaginaAbilitata,
    ruoloMappato
  } = useAutorizzazioni(utente);

  // Hook per gestire la gerarchia della rete
  const { userNode, subordinateNodes, loading: hierarchyLoading } = useNetworkHierarchy(utente);

  // Stato per i dati fatturato
  const [statFatturato, setStatFatturato] = useState({ fatturatoPerMese: [] });
  // Stato per i dati pratiche
  const [statPratiche, setStatPratiche] = useState({ pratichePerMese: [] });
  // Stato per animazioni
  const [isLoaded, setIsLoaded] = useState(false);
  // Stato per refresh
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Funzione per caricare dati fatturato
  const caricaFatturato = async () => {
    try {
      console.log('📊 Caricamento statistiche fatturato per dashboard NLT');
      const response = await api.get(`/statistiche/fatturato`, {
        params: {
          dataInizio: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
          dataFine: new Date().toISOString().split('T')[0],
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data && response.data.success) {
        setStatFatturato(response.data.statistiche);
        console.log('✅ Statistiche fatturato caricate:', response.data.statistiche);
      }
    } catch (error) {
      console.error('❌ Errore caricamento statistiche fatturato:', error);
      setStatFatturato({ fatturatoPerMese: [] });
    }
  };

  // Funzione per caricare dati pratiche
  const caricaPratiche = async () => {
    try {
      console.log('📊 Caricamento statistiche pratiche per dashboard NLT');
      const response = await api.get(`/statistiche/pratiche`, {
        params: {
          dataInizio: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
          dataFine: new Date().toISOString().split('T')[0],
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data && response.data.success) {
        setStatPratiche(response.data.statistiche);
        //console.log('✅ Statistiche pratiche caricate:', response.data.statistiche);
      }
    } catch (error) {
      console.error('❌ Errore caricamento statistiche pratiche:', error);
      setStatPratiche({ pratichePerMese: [] });
    }
  };

  // Effect per caricamento iniziale
  useEffect(() => {
    // Non caricare statistiche se la gerarchia non è ancora caricata
    if (hierarchyLoading) {
      console.log('⏳ Attendo caricamento gerarchia prima di caricare statistiche dashboard...');
      return;
    }

    // Carica dati iniziali
    caricaFatturato();
    caricaPratiche();
    
    // Trigger animazioni dopo il caricamento
    setTimeout(() => setIsLoaded(true), 300);
  }, [hierarchyLoading]);

  // Effect per aggiornamento periodico automatico (ogni 5 minuti)
  useEffect(() => {
    // Non avviare il polling se la gerarchia non è caricata
    if (hierarchyLoading) return;

    console.log('🔄 Avvio aggiornamento automatico statistiche (ogni 5 minuti)');
    
    // Aggiorna le statistiche ogni 5 minuti
    const intervalId = setInterval(() => {
      console.log('🔄 Aggiornamento automatico statistiche...');
      caricaFatturato();
      caricaPratiche();
    }, 5 * 60 * 1000); // 5 minuti

    // Cleanup: rimuovi interval quando il componente si smonta
    return () => {
      console.log('🛑 Fermato aggiornamento automatico statistiche');
      clearInterval(intervalId);
    };
  }, [hierarchyLoading]);

  // Effect per ricaricare quando la pagina diventa visibile
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !hierarchyLoading) {
        console.log('👁️ Pagina tornata visibile, ricarico statistiche...');
        caricaFatturato();
        caricaPratiche();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hierarchyLoading]);

  // Funzione per le azioni rapide filtrate per ruolo
  const getAzioniRapidePerRuolo = () => {
    const tutteAzioni = [
      {
        id: 'nuovo-cliente',
        titolo: 'Nuovo Cliente',
        descrizione: 'Aggiungi cliente',
        icona: <UserPlusIcon className="h-5 w-5" />,
        link: '/nlt/clienti/nuovo',
        abilitata: isSottopaginaAbilitata('clienti', 'lista-clienti'),
        ruoli: ['admin', 'agenzia', 'subagente', 'team_leader', 'responsabile_area', 'responsabile_zona'],
        colore: 'from-blue-500 to-blue-600',
        categoria: 'primary'
      },
      {
        id: 'nuova-quotazione',
        titolo: 'Nuova Quotazione',
        descrizione: 'Crea preventivo',
        icona: <CalculatorIcon className="h-5 w-5" />,
        link: '/nlt/quotazioni',
        abilitata: isSottopaginaAbilitata('quotazioni', 'lista-quotazioni'),
        ruoli: ['admin', 'agenzia', 'subagente', 'team_leader', 'responsabile_area', 'responsabile_zona'],
        colore: 'from-emerald-500 to-emerald-600',
        categoria: 'primary'
      },
      {
        id: 'pratiche',
        titolo: 'Pratiche Attive',
        descrizione: 'Gestisci pratiche',
        icona: <ClipboardDocumentListIcon className="h-5 w-5" />,
        link: '/nlt/pratiche',
        abilitata: isSottopaginaAbilitata('quotazioni', 'pratiche'),
        ruoli: ['admin', 'agenzia', 'subagente', 'team_leader', 'responsabile_area', 'responsabile_zona'],
        colore: 'from-purple-500 to-purple-600',
        categoria: 'primary'
      },
      {
        id: 'lead',
        titolo: 'Gestione Lead',
        descrizione: 'Nuove opportunità',
        icona: <SparklesIcon className="h-5 w-5" />,
        link: '/nlt/lead',
        abilitata: isSottopaginaAbilitata('clienti', 'gestione-lead'),
        ruoli: ['admin', 'agenzia', 'subagente', 'team_leader', 'responsabile_area', 'responsabile_zona'],
        colore: 'from-orange-500 to-orange-600',
        categoria: 'secondary'
      },
      {
        id: 'clienti',
        titolo: 'Lista Clienti',
        descrizione: 'Tutti i clienti',
        icona: <UsersIcon className="h-5 w-5" />,
        link: '/nlt/clienti',
        abilitata: isSottopaginaAbilitata('clienti', 'lista-clienti'),
        ruoli: ['admin', 'agenzia', 'subagente', 'team_leader', 'responsabile_area', 'responsabile_zona'],
        colore: 'from-indigo-500 to-indigo-600',
        categoria: 'secondary'
      },
      {
        id: 'contratti',
        titolo: 'Contratti',
        descrizione: 'Gestisci contratti attivi',
        icona: <DocumentTextIcon className="h-5 w-5" />,
        link: '/nlt/contratti',
        abilitata: isSottopaginaAbilitata('gestione', 'contratti'),
        ruoli: ['admin', 'agenzia', 'team_leader', 'responsabile_area', 'responsabile_zona', 'subagente', 'agente', 'agente_commerciale'],
        colore: 'from-rose-500 to-rose-600',
        categoria: 'secondary'
      },
      {
        id: 'statistiche',
        titolo: 'Statistiche',
        descrizione: 'Analisi performance',
        icona: <ChartBarIcon className="h-5 w-5" />,
        link: '/nlt/statistiche-fatturato',
        abilitata: isSottopaginaAbilitata('statistiche', 'statistiche-fatturato'),
        ruoli: ['admin', 'agenzia', 'team_leader', 'responsabile_area', 'responsabile_zona'],
        colore: 'from-teal-500 to-teal-600',
        categoria: 'secondary'
      },
      {
        id: 'rete',
        titolo: 'Dashboard Rete',
        descrizione: 'Struttura rete',
        icona: <BuildingOfficeIcon className="h-5 w-5" />,
        link: '/rete-multilivello',
        abilitata: isSottopaginaAbilitata('rete-multilivello', 'dashboard-rete'),
        ruoli: ['admin', 'agenzia', 'team_leader', 'responsabile_area', 'responsabile_zona'],
        colore: 'from-cyan-500 to-cyan-600',
        categoria: 'secondary'
      }
    ];

    const azioniFiltrate = tutteAzioni.filter(azione =>
      azione.abilitata && azione.ruoli.includes(ruoloMappato)
    );
    
    return azioniFiltrate;
  };

  const azioniRapide = getAzioniRapidePerRuolo();
  const azioniPrimarie = azioniRapide.filter(a => a.categoria === 'primary');
  const azioniSecondarie = azioniRapide.filter(a => a.categoria === 'secondary');

  // Configurazione grafico fatturato mensile con stile Apple
  const chartFatturatoMensile = {
    labels: statFatturato.fatturatoPerMese?.map(item => item.mese) || [],
    datasets: [
      {
        label: 'Fatturato €',
        data: statFatturato.fatturatoPerMese?.map(item => item.fatturato) || [],
        backgroundColor: 'rgba(0, 122, 255, 0.1)',
        borderColor: 'rgba(0, 122, 255, 1)',
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgba(0, 122, 255, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      }
    ]
  };

  const chartOptionsFatturato = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: false,
        titleFont: { size: 14, weight: '600' },
        bodyFont: { size: 13 },
        padding: 12
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          color: 'rgba(0, 0, 0, 0.6)',
          font: { size: 12, weight: '500' },
          padding: 12,
          callback: function(value) {
            return new Intl.NumberFormat('it-IT', { 
              style: 'currency', 
              currency: 'EUR', 
              minimumFractionDigits: 0, 
              maximumFractionDigits: 0 
            }).format(value);
          }
        }
      },
      x: {
        border: { display: false },
        grid: { display: false },
        ticks: {
          color: 'rgba(0, 0, 0, 0.6)',
          font: { size: 12, weight: '500' },
          padding: 8
        }
      }
    },
  };

  // Configurazione grafico pratiche mensili con stile Apple
  const chartPraticheMensili = {
    labels: statPratiche.pratichePerMese?.map(item => item.mese) || [],
    datasets: [
      {
        type: 'bar',
        label: 'Pratiche Create',
        data: statPratiche.pratichePerMese?.map(item => item.create) || [],
        backgroundColor: 'rgba(52, 199, 89, 0.8)',
        borderColor: 'rgba(52, 199, 89, 1)',
        borderWidth: 0,
        borderRadius: 6,
        borderSkipped: false,
      },
      {
        type: 'bar',
        label: 'Pratiche Completate',
        data: statPratiche.pratichePerMese?.map(item => item.completate) || [],
        backgroundColor: 'rgba(0, 122, 255, 0.8)',
        borderColor: 'rgba(0, 122, 255, 1)',
        borderWidth: 0,
        borderRadius: 6,
        borderSkipped: false,
      }
    ]
  };

  const chartOptionsPratiche = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: 'rgba(0, 0, 0, 0.8)',
          font: { size: 12, weight: '500' },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 12,
        displayColors: false,
        titleFont: { size: 14, weight: '600' },
        bodyFont: { size: 13 },
        padding: 12
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          color: 'rgba(0, 0, 0, 0.6)',
          font: { size: 12, weight: '500' },
          padding: 12
        }
      },
      x: {
        border: { display: false },
        grid: { display: false },
        ticks: {
          color: 'rgba(0, 0, 0, 0.6)',
          font: { size: 12, weight: '500' },
          padding: 8
        }
      }
    },
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'admin': 'Amministratore',
      'agenzia': 'Agenzia',
      'team_leader': 'Team Leader',
      'subagente': 'Agente',
      'agente': 'Agente',
      'responsabile_area': 'Resp. Area',
      'responsabile_zona': 'Resp. Zona'
    };
    return roleMap[role] || role;
  };

  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buongiorno';
    if (hour < 18) return 'Buon pomeriggio';
    return 'Buonasera';
  };

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,122,255,0.05)_1px,transparent_0)] [background-size:24px_24px] pointer-events-none" />
      
      <div className="relative z-10 h-full flex flex-col px-6 py-6">
        {/* Header Section */}
        <div className={`flex-shrink-0 transition-all duration-1000 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/5 mb-6">
            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-blue-500/25">
                      <RocketLaunchIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent mb-1">
                      {getTimeGreeting()}, {utente?.nome}
                    </h1>
                    <p className="text-gray-600 font-medium">
                      Benvenuto nella tua dashboard NLT
                    </p>
                  </div>
                </div>
              <div className="hidden sm:flex items-center space-x-4">
                {/* Pulsante Refresh Statistiche */}
                <button
                  onClick={async () => {
                    setIsRefreshing(true);
                    console.log('🔄 Refresh manuale statistiche');
                    await Promise.all([caricaFatturato(), caricaPratiche()]);
                    setTimeout(() => setIsRefreshing(false), 500);
                  }}
                  disabled={isRefreshing}
                  className={`p-3 rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 transition-all duration-200 group border border-blue-200/50 ${isRefreshing ? 'cursor-not-allowed opacity-50' : ''}`}
                  title="Aggiorna statistiche"
                >
                  <ArrowPathIcon className={`h-5 w-5 text-blue-600 transition-transform duration-500 ${isRefreshing ? 'animate-spin' : 'group-hover:rotate-180'}`} />
                </button>

                <div className="text-right">
                  <div className="text-sm text-gray-500 font-medium">Il tuo ruolo</div>
                  <div className="text-lg font-semibold text-gray-900">{getRoleDisplayName(ruoloMappato)}</div>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {utente?.nome?.charAt(0)}{utente?.cognome?.charAt(0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">
          {/* Left Column - Charts */}
          <div className="lg:col-span-8 flex flex-col space-y-6 min-h-0">
            {/* Quick Actions - Primary */}
            <div className={`flex-shrink-0 transition-all duration-1000 ease-out delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-1">Azioni Principali</h2>
                <p className="text-gray-600 text-sm">Accessi rapidi alle funzioni più utilizzate</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {azioniPrimarie.map((azione, index) => (
                  <Link
                    key={azione.id}
                    to={azione.link}
                    className={`group block transition-all duration-300 ease-out delay-${index * 100}`}
                  >
                    <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-4 border border-white/20 shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-black/10 hover:scale-[1.02] transition-all duration-300">
                                              <div className="flex items-center space-x-3">
                          <div className={`w-12 h-12 bg-gradient-to-br ${azione.colore} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                            <div className="text-white">
                              {azione.icona}
                            </div>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 mb-1">
                              {azione.titolo}
                            </h3>
                            <p className="text-gray-600 text-xs">
                              {azione.descrizione}
                            </p>
                          </div>
                        <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Charts Section */}
            <div className={`flex-1 transition-all duration-1000 ease-out delay-400 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} min-h-0 max-h-[500px]`}>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 h-full">
                {/* Fatturato Chart */}
                <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/5 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <CurrencyEuroIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Fatturato Mensile</h3>
                        <p className="text-gray-600 text-xs">Andamento anno corrente</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-gray-900">
                        €{(statFatturato.fatturatoPerMese?.[statFatturato.fatturatoPerMese.length - 1]?.fatturato || 0).toLocaleString('it-IT')}
                      </div>
                      <div className="text-xs text-gray-500 font-medium">{statFatturato.fatturatoPerMese?.[statFatturato.fatturatoPerMese.length - 1]?.mese || 'Mese corrente'}</div>
                    </div>
                  </div>
                  <div className="flex-1 min-h-0">
                    <Line data={chartFatturatoMensile} options={chartOptionsFatturato} />
                  </div>
                </div>

                {/* Pratiche Chart */}
                <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-xl shadow-black/5 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <ClipboardDocumentListIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Pratiche Mensili</h3>
                        <p className="text-gray-600 text-xs">Create vs Completate</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-gray-900">
                        {(statPratiche.pratichePerMese?.reduce((sum, item) => sum + (item.create || 0), 0) || 0)}
                      </div>
                      <div className="text-xs text-blue-600 font-medium">Pratiche totali</div>
                    </div>
                  </div>
                  <div className="flex-1 min-h-0">
                    <Bar data={chartPraticheMensili} options={chartOptionsPratiche} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Actions & Info */}
          <div className="lg:col-span-4 flex flex-col space-y-4 min-h-0">
            {/* Secondary Actions */}
            <div className={`flex-1 transition-all duration-1000 ease-out delay-600 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} min-h-0`}>
              <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-4 border border-white/20 shadow-xl shadow-black/5 h-full flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-6 h-6 bg-gradient-to-br from-gray-600 to-gray-700 rounded-lg flex items-center justify-center">
                    <LightBulbIcon className="h-4 w-4 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900">Altre Funzioni</h3>
                </div>
                <div className="space-y-2 flex-1 overflow-y-auto apple-scrollbar">
                  {azioniSecondarie.map((azione, index) => (
                    <Link
                      key={azione.id}
                      to={azione.link}
                      className="group block"
                    >
                      <div className="flex items-center space-x-3 p-3 rounded-xl hover:bg-white/50 transition-all duration-300">
                        <div className={`w-8 h-8 bg-gradient-to-br ${azione.colore} rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                          <div className="text-white">
                            {azione.icona}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 text-sm">
                            {azione.titolo}
                          </h4>
                          <p className="text-gray-600 text-xs">
                            {azione.descrizione}
                          </p>
                        </div>
                        <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className={`flex-shrink-0 transition-all duration-1000 ease-out delay-800 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-4 border border-white/20 shadow-xl shadow-black/5">
                <h3 className="font-bold text-gray-900 mb-4">Riepilogo Rapido</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center">
                        <CheckCircleIcon className="h-4 w-4 text-white" />
                      </div>
                      <span className="font-medium text-gray-900 text-sm">Pratiche Attive</span>
                    </div>
                    <span className="font-bold text-blue-600 text-sm">
                      {statPratiche.pratichePerMese?.reduce((sum, item) => sum + (item.create || 0) - (item.completate || 0), 0) || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-green-100/50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center">
                        <CurrencyEuroIcon className="h-4 w-4 text-white" />
                      </div>
                      <span className="font-medium text-gray-900 text-sm">Fatturato Medio</span>
                    </div>
                    <span className="font-bold text-green-600 text-sm">
                      €{Math.round((statFatturato.fatturatoPerMese?.reduce((sum, item) => sum + (item.fatturato || 0), 0) || 0) / Math.max(statFatturato.fatturatoPerMese?.length || 1, 1)).toLocaleString('it-IT')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-purple-100/50 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 bg-purple-500 rounded-lg flex items-center justify-center">
                        <ArrowTrendingUpIcon className="h-4 w-4 text-white" />
                      </div>
                      <span className="font-medium text-gray-900 text-sm">Tasso Successo</span>
                    </div>
                    <span className="font-bold text-purple-600 text-sm">
                      {Math.round(((statPratiche.pratichePerMese?.reduce((sum, item) => sum + (item.completate || 0), 0) || 0) / Math.max(statPratiche.pratichePerMese?.reduce((sum, item) => sum + (item.create || 0), 0) || 1, 1)) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardNlt; 